import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationStore } from '../../stores/useNavigationStore';
import { useTeamStore } from '../../stores/useTeamStore';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { useAchievementsStore } from '../../stores/useAchievementsStore';
import { useReferralNotifications } from '../../hooks/useReferralNotifications';
import styles from './BottomNav.module.css';
import clsx from 'clsx';

import StarIcon from '../../assets/icons/Star.svg';
import { ChatIcon, ImageIcon, VideoIcon, ClipsIcon, AccountIcon, BackIcon, EditVideoIcon, CreateVideoIcon } from './NavIcons';
import { useVideoStore } from '../../stores/useVideoStore';
import { EDIT_MODEL_IDS } from '../../components/Video/VideoComposer/components/VideoModelSelectorChip';
import { DEFAULT_VIDEO_PROVIDER, DEFAULT_VIDEO_MODEL } from '../../config/models';
import type { FC, SVGProps } from 'react';

const BottomNav = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { isMenuHidden } = useNavigationStore();
  const { limits } = useSubscriptionLimits();
  const { haptic } = useTelegramWebApp();
  const { hasUnreadAchievements } = useAchievementsStore();
  const { unviewedCount } = useReferralNotifications();
  const menuRef = useRef<HTMLElement>(null);

  // Video page expansion state
  const sidebarActiveTab = useVideoStore(s => s.sidebarActiveTab);
  const setSidebarActiveTab = useVideoStore(s => s.setSidebarActiveTab);
  const prevPathRef = useRef<string>('/chat');

  // Состояние перетаскивания
  const [isDragging, setIsDragging] = useState(false);
  const dragTargetRef = useRef<HTMLElement | null>(null);

  // Walk offsetParent chain to get position relative to menuRef (works across nested groups)
  const getTargetLeftAndWidth = useCallback((targetEl: HTMLElement) => {
    let left = targetEl.offsetLeft;
    let parent = targetEl.offsetParent as HTMLElement | null;
    while (parent && parent !== menuRef.current) {
      left += parent.offsetLeft;
      parent = parent.offsetParent as HTMLElement | null;
    }
    return { left, width: targetEl.offsetWidth };
  }, []);

  // Handle Drag Move (follow finger)
  const handleDragMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging || !menuRef.current) return;
    e.preventDefault(); // prevent scroll
    
    let clientX;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }
    
    // Ищем элемент под пальцем
    const elements = document.elementsFromPoint(clientX, menuRef.current.getBoundingClientRect().top + (menuRef.current.clientHeight / 2));
    const itemEl = elements.find(el => 
      el.classList.contains(styles.menuItem) || 
      el.classList.contains(styles.logoItem) || 
      el.classList.contains(styles.accountItem)
    ) as HTMLElement;
    
    if (itemEl) {
      // Если элемент заблокирован - игнорируем
      if (itemEl.classList.contains(styles.disabled)) return;
      
      dragTargetRef.current = itemEl;
      const { left, width } = getTargetLeftAndWidth(itemEl);
      
      const indicator = menuRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
      if (indicator) {
        // Убираем анимацию во время перетаскивания
        indicator.classList.add(styles.dragging);
        menuRef.current.style.setProperty('--active-left', `${left}px`);
        menuRef.current.style.setProperty('--active-width', `${width}px`);
      }
    }
  }, [isDragging, getTargetLeftAndWidth]);

  // Handle Drag End (apply target)
  const handleDragEnd = useCallback(() => {
    if (!isDragging || !menuRef.current) return;
    setIsDragging(false);
    
    const indicator = menuRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
    if (indicator) {
      indicator.classList.remove(styles.dragging);
    }
    
    // Если остановились на новом элементе, имитируем клик по нему
    if (dragTargetRef.current) {
      // Имитируем клик
      const clickEvent = new MouseEvent('click', { bubbles: true });
      dragTargetRef.current.dispatchEvent(clickEvent);
      // Возвращаем фокус на настоящий активный если мы просто отпустили
      setTimeout(() => {
        const activeEl = menuRef.current?.querySelector(`.${styles.active}, .${styles.logoActive}`) as HTMLElement;
        if (activeEl && menuRef.current) {
           const { left, width } = getTargetLeftAndWidth(activeEl);
           menuRef.current.style.setProperty('--active-left', `${left}px`);
           menuRef.current.style.setProperty('--active-width', `${width}px`);
        }
      }, 50);
    }
  }, [isDragging, getTargetLeftAndWidth]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleDragMove as any, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      window.addEventListener('mousemove', handleDragMove as any);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('touchmove', handleDragMove as any);
      window.removeEventListener('touchend', handleDragEnd);
      window.removeEventListener('mousemove', handleDragMove as any);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  // Проверяем наличие непрочитанных достижений
  const hasUnread = hasUnreadAchievements();
  
  // Проверяем наличие новых активированных рефералов
  const hasNewReferrals = unviewedCount > 0;

  // Team mode — единственный источник истины: Zustand store (persisted в localStorage)
  // Переключение только через ProfileScreen (с полной перезагрузкой)
  const { isTeamMode } = useTeamStore();
  // Определяем активную страницу независимо от режима
  const getActivePage = () => {
    const path = location.pathname;
    if (path.includes('/chat')) return 'chat';
    if (path.includes('/image')) return 'image';
    if (path.includes('/video')) return 'video';
    if (path.includes('/tools')) return 'clips';
    if (path === '/account') return 'account';
    return null;
  };
  const activePage = getActivePage();

  // Пути всегда чистые — team mode определяется стором, не URL
  const getPath = (basePath: string) => basePath;

  // Основные пункты меню (без аккаунта)
  const menuItems: { id: string; basePath: string; Icon: FC<SVGProps<SVGSVGElement>>; label: string }[] = [
    { id: 'chat', basePath: '/chat', Icon: ChatIcon, label: t('nav.chat') },
    { id: 'image', basePath: '/image', Icon: ImageIcon, label: t('nav.image') },
    { id: 'video', basePath: '/video', Icon: VideoIcon, label: t('nav.video') },
    { id: 'clips', basePath: '/tools', Icon: ClipsIcon, label: t('nav.tools') },
  ];

  // Аккаунт отдельно
  const accountItem = { id: 'account', basePath: '/account', label: t('nav.account') };

  // Проверяем, находимся ли на главной странице
  const isOnLanding = location.pathname === '/';

  // Проверка доступности видео
  const isVideoAvailable = limits?.video?.enabled !== false;
  
  // Safe area теперь полностью управляется через CSS переменные (--safe-top/bottom/left/right)
  // telegram-web-app.js автоматически устанавливает --tg-safe-area-inset-* и --tg-content-safe-area-inset-* на :root

  const isAccountActive = activePage === 'account';
  const isVideoPage = activePage === 'video';
  const prevIsVideoRef = useRef(isVideoPage);

  // Track previous non-video path for back navigation
  useEffect(() => {
    if (activePage && activePage !== 'video') {
      prevPathRef.current = location.pathname;
    }
  }, [activePage, location.pathname]);

  // Эффект для обновления позиции плавающего фона
  useEffect(() => {
    const isPageTransition = prevIsVideoRef.current !== isVideoPage;
    prevIsVideoRef.current = isVideoPage;

    const recalc = () => {
      if (!menuRef.current) return;
      const activeEl = menuRef.current.querySelector(`.${styles.active}, .${styles.logoActive}`) as HTMLElement;

      if (activeEl) {
        // Walk offsetParent chain for correct position across nested groups
        let rawLeft = activeEl.offsetLeft;
        let parent = activeEl.offsetParent as HTMLElement | null;
        while (parent && parent !== menuRef.current) {
          rawLeft += parent.offsetLeft;
          parent = parent.offsetParent as HTMLElement | null;
        }
        const newLeft = `${rawLeft}px`;
        const newWidth = `${activeEl.offsetWidth}px`;

        const indicator = menuRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
        if (indicator && !isPageTransition) {
          const currentLeft = menuRef.current.style.getPropertyValue('--active-left');
          if (currentLeft && currentLeft !== newLeft) {
            indicator.classList.add(styles.moving);
            clearTimeout((indicator as any)._squishTimer);
            (indicator as any)._squishTimer = setTimeout(() => {
              indicator.classList.remove(styles.moving);
            }, 150);
          }
        }

        menuRef.current.style.setProperty('--active-left', newLeft);
        menuRef.current.style.setProperty('--active-width', newWidth);
      } else {
        menuRef.current.style.setProperty('--active-width', '0px');
      }
    };

    if (isPageTransition && menuRef.current) {
      // При переходе в/из видео — плавно скрываем индикатор,
      // после завершения CSS-анимации показываем fade-in на новом месте
      const indicator = menuRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
      if (indicator) {
        indicator.style.opacity = '0';
        indicator.style.transition = 'opacity 0.15s ease';
      }

      const timer = setTimeout(() => {
        // Ставим позицию без анимации
        if (indicator) indicator.style.transition = 'none';
        recalc();
        // Force reflow, затем включаем fade-in
        requestAnimationFrame(() => {
          if (indicator) {
            indicator.style.transition = '';
            indicator.style.opacity = '';
          }
        });
      }, 320);
      return () => clearTimeout(timer);
    }

    // Обычный пересчёт — короткая задержка для обновления DOM
    const timer = setTimeout(recalc, 20);
    return () => clearTimeout(timer);
  }, [activePage, isOnLanding, isAccountActive, location.pathname, isVideoPage, sidebarActiveTab]);

  // Video expanded nav handlers
  const handleVideoBack = useCallback(() => {
    if (isDragging) return;
    haptic.impact('medium');
    navigate(prevPathRef.current || getPath('/chat'));
  }, [isDragging, haptic, navigate, getPath]);

  const handleVideoSubTabClick = useCallback((subTab: 'create' | 'edit') => () => {
    if (isDragging) return;
    haptic.impact('medium');
    setSidebarActiveTab(subTab);

    const store = useVideoStore.getState();
    if (subTab === 'create') {
      // Reset from edit model back to default create model
      if (EDIT_MODEL_IDS.includes(store.currentModel)) {
        store.setProvider(DEFAULT_VIDEO_PROVIDER);
        store.setModel(DEFAULT_VIDEO_MODEL);
        store.setGenerationMode('standard');
      }
    } else {
      // Switch to edit model if not already on one
      if (!EDIT_MODEL_IDS.includes(store.currentModel)) {
        store.setProvider('kling');
        store.setModel('kling-o3');
        store.setGenerationMode('video2video');
      }
    }
  }, [isDragging, haptic, setSidebarActiveTab]);

  const handleItemClick = (itemId: string, basePath: string) => () => {
    // Предотвращаем срабатывание клика после перетаскивания (если оно было)
    if (isDragging) return;
    
    // Haptic feedback при клике на навигацию
    haptic.impact('medium');
    
    const targetPath = getPath(basePath);
    
    // Блокировка видео для Free подписки (только в personal режиме)
    if (itemId === 'video' && !isVideoAvailable && !isTeamMode) {
      navigate('/pricing');
      return;
    }
    
    // Если уже находимся на странице аккаунта и нажали на кнопку аккаунта,
    // отправляем событие для возврата в главное меню профиля
    if (basePath === '/account' && location.pathname === '/account') {
      window.dispatchEvent(new CustomEvent('reset-account-screen'));
      return;
    }
    
    navigate(targetPath);
    
    // Отправляем событие о смене страницы для обновления счётчиков
    window.dispatchEvent(new CustomEvent('navigation-change'));
  };

  const handleLogoClick = () => {
    if (isDragging) return;
    haptic.impact('light');
    navigate('/');
  };

  return (
    <>
      <nav 
        ref={menuRef}
        className={styles.mainMenu} 
        data-menu="main"
        data-hidden={isMenuHidden ? 'true' : 'false'}
      >
        {/* Индикатор текущего активного элемента */}
        <div className={styles.activeIndicator} style={{
          transform: `translate3d(var(--active-left, 0px), -50%, 0)`,
          width: `var(--active-width, 0px)`
        }} />

        {/* Nav content: overlapping normal + video groups */}
        <div className={styles.navContent}>
          {/* Normal group: logo + 4 items — slides LEFT on video page */}
          <div className={clsx(styles.normalGroup, { [styles.normalGroupSlideOut]: isVideoPage })}>
            {/* Логотип */}
            <div
              className={clsx(styles.logoItem, { [styles.logoActive]: isOnLanding && !isVideoPage })}
              onClick={handleLogoClick}
              onMouseDown={() => setIsDragging(true)}
              onTouchStart={() => setIsDragging(true)}
              role="button"
              tabIndex={0}
              aria-label={t('nav.home')}
            >
              <img
                src={StarIcon}
                alt="Star AI"
                className={styles.logoIcon}
                style={{ filter: isOnLanding ? 'none' : 'grayscale(100%) brightness(0.6)' }}
              />
            </div>

            {/* Основные пункты навигации */}
            {menuItems.map((item) => {
              const isActive = activePage === item.id;
              const isDisabled = item.id === 'video' && !isVideoAvailable && !isTeamMode;

              return (
                <div
                  key={item.id}
                  className={clsx(styles.menuItem, {
                    [styles.active]: isActive && !isVideoPage,
                    [styles.disabled]: isDisabled,
                  })}
                  onClick={handleItemClick(item.id, item.basePath)}
                  onMouseDown={() => {
                    if (!isDisabled) setIsDragging(true);
                  }}
                  onTouchStart={() => {
                    if (!isDisabled) setIsDragging(true);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={item.label}
                >
                  <div className={styles.menuIcon}>
                    <item.Icon className={`${styles.icon} ${isActive && !isVideoPage ? styles.iconActive : ''}`} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Video group: back + create + edit — slides UP on video page */}
          <div className={clsx(styles.videoGroup, { [styles.videoGroupSlideIn]: isVideoPage })}>
            {/* Кнопка назад */}
            <div
              className={styles.logoItem}
              onClick={handleVideoBack}
              onMouseDown={() => setIsDragging(true)}
              onTouchStart={() => setIsDragging(true)}
              role="button"
              tabIndex={0}
              aria-label="Назад"
            >
              <BackIcon className={styles.backIcon} />
            </div>

            {/* Создать видео */}
            <div
              className={clsx(styles.menuItem, styles.videoSubItem, {
                [styles.active]: isVideoPage && sidebarActiveTab === 'create',
              })}
              onClick={handleVideoSubTabClick('create')}
              onMouseDown={() => setIsDragging(true)}
              onTouchStart={() => setIsDragging(true)}
              role="button"
              tabIndex={0}
              aria-label="Создать видео"
            >
              <div className={styles.menuIcon}>
                <CreateVideoIcon className={clsx(styles.icon, { [styles.iconActive]: isVideoPage && sidebarActiveTab === 'create' })} />
              </div>
              <span className={styles.subLabel}>Создать</span>
            </div>

            {/* Редактирование видео */}
            <div
              className={clsx(styles.menuItem, styles.videoSubItem, {
                [styles.active]: isVideoPage && sidebarActiveTab === 'edit',
              })}
              onClick={handleVideoSubTabClick('edit')}
              onMouseDown={() => setIsDragging(true)}
              onTouchStart={() => setIsDragging(true)}
              role="button"
              tabIndex={0}
              aria-label="Редактирование видео"
            >
              <div className={styles.menuIcon}>
                <EditVideoIcon className={clsx(styles.icon, { [styles.iconActive]: isVideoPage && sidebarActiveTab === 'edit' })} />
              </div>
              <span className={styles.subLabel}>Ред.</span>
            </div>
          </div>
        </div>

        {/* Аккаунт справа — stays in place */}
        <div className={styles.accountBlock}>
          <div
            className={clsx(styles.accountItem, { [styles.active]: isAccountActive })}
            onClick={handleItemClick(accountItem.id, accountItem.basePath)}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            role="button"
            tabIndex={0}
            aria-label={accountItem.label}
          >
            <div className={styles.menuIcon}>
              <AccountIcon className={`${styles.icon} ${isAccountActive ? styles.iconActive : ''}`} />
              {/* Индикатор непрочитанных достижений */}
              {hasUnread && (
                <div className={styles.unreadBadge}></div>
              )}
              {/* Индикатор новых активированных рефералов */}
              {hasNewReferrals && (
                <div className={styles.referralBadge}></div>
              )}
            </div>
          </div>
        </div>
      </nav>
      
    </>
  );
};

export default BottomNav;

